import React, { useEffect, useRef } from "react";
import * as d3 from "d3";

export type PieDatum = {
  label: string;
  value: number;
  color: string;
};

type Props = {
  data: PieDatum[];
  width?: number;
  height?: number;
  innerRadius?: number;
  legend?: boolean;
};

/**
 * D3 pie/donut chart with proper arcs and optional legend.
 */
export default function D3PieChart({
  data,
  width = 280,
  height = 280,
  innerRadius = 56,
  legend = true,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || !data.length) return;

    const total = data.reduce((s, d) => s + (Number.isFinite(d.value) ? d.value : 0), 0) || 1;
    const radius = Math.min(width, height) / 2 - 12;
    const outerRadius = radius;
    const innerR = Math.min(innerRadius, outerRadius - 8);

    const pie = d3
      .pie<PieDatum>()
      .value((d) => Math.max(0, d.value))
      .sort(null);

    const arc = d3
      .arc<d3.PieArcDatum<PieDatum>>()
      .innerRadius(innerR)
      .outerRadius(outerRadius);

    const arcLabel = d3
      .arc<d3.PieArcDatum<PieDatum>>()
      .innerRadius(outerRadius * 0.6)
      .outerRadius(outerRadius * 0.6);

    d3.select(containerRef.current).selectAll("*").remove();

    const svg = d3
      .select(containerRef.current)
      .append("svg")
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("role", "img")
      .attr("aria-label", "Pie chart");

    const g = svg.append("g").attr("transform", `translate(${width / 2},${height / 2})`);

    const arcs = pie(data);

    // Arcs
    g.selectAll(".arc")
      .data(arcs)
      .join("path")
      .attr("class", "arc")
      .attr("d", arc)
      .attr("fill", (d) => d.data.color)
      .attr("stroke", "currentColor")
      .attr("stroke-opacity", 0.15)
      .attr("stroke-width", 1.5);

    // Labels (percentage) on slices
    g.selectAll(".arc-label")
      .data(arcs)
      .join("text")
      .attr("class", "arc-label")
      .attr("transform", (d) => {
        const c = arcLabel.centroid(d);
        return `translate(${c[0]},${c[1]})`;
      })
      .attr("text-anchor", "middle")
      .attr("font-size", 11)
      .attr("font-weight", 600)
      .attr("fill", "currentColor")
      .text((d) => {
        const pct = total > 0 ? Math.round((d.data.value / total) * 100) : 0;
        return pct > 0 ? `${pct}%` : "";
      });

    return () => {
      d3.select(containerRef.current).selectAll("*").remove();
    };
  }, [data, width, height, innerRadius]);

  return (
    <div className="flex flex-col items-center gap-3 w-full">
      <div
        ref={containerRef}
        className="min-h-[240px] w-full max-w-[280px]"
        style={{ color: "var(--foreground)" }}
      />
      {legend && data.length > 0 && (
        <div className="flex flex-wrap justify-center gap-3 text-xs">
          {data.map((d) => (
            <div key={d.label} className="flex items-center gap-2">
              <span
                className="inline-block w-3 h-3 rounded-sm shrink-0"
                style={{ backgroundColor: d.color }}
              />
              <span className="text-muted-foreground">
                {d.label.toLowerCase() === "failed" ? "success" : d.label}: {d.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
